import { fetchAttachmentBlob, getThread, listThreads } from './api';
import type {
  MailboxAttachment,
  MailboxParticipant,
  MailboxSummary,
  MailboxThread,
  MailboxView,
  ThreadFolder,
} from './types';

export type ThreadDetail = Awaited<ReturnType<typeof getThread>>;
export type ThreadListPage = { threads: MailboxThread[]; nextCursor?: string };

const SUMMARY_KEY = 'cwy.mailbox.summary';

let unreadCache: number | null = null;
const lists = new Map<string, ThreadListPage>();
const details = new Map<string, ThreadDetail>();
const detailFetchedAt = new Map<string, number>();
const DETAIL_FRESH_MS = 30_000;
const inflight = new Map<string, Promise<ThreadDetail>>();

const listKey = (mailboxId: string, view: MailboxView) => `${mailboxId}:${view}`;
const detailKey = (mailboxId: string, threadId: string) => `${mailboxId}:${threadId}`;

const currentUserId = () => localStorage.getItem('userId') || '';

export function getCachedSummary(): MailboxSummary | null {
  try {
    const raw = sessionStorage.getItem(SUMMARY_KEY);
    const parsed = raw ? (JSON.parse(raw) as { userId?: string; mailbox?: MailboxSummary }) : null;
    const mailbox = parsed?.mailbox;
    if (!mailbox?.id || !mailbox.address || !parsed?.userId || parsed.userId !== currentUserId()) return null;
    return mailbox;
  } catch {
    return null;
  }
}

export function setCachedSummary(summary: MailboxSummary | null) {
  try {
    const userId = currentUserId();
    if (summary && userId) sessionStorage.setItem(SUMMARY_KEY, JSON.stringify({ userId, mailbox: summary }));
    else sessionStorage.removeItem(SUMMARY_KEY);
  } catch {
    /* storage unavailable */
  }
}

export function getCachedUnread(): number {
  return unreadCache ?? 0;
}

export function setCachedUnread(count: number) {
  unreadCache = count;
}

export function clearMailboxCache() {
  setCachedSummary(null);
  unreadCache = null;
  lists.clear();
  persistLists();
  details.clear();
  detailFetchedAt.clear();
  inflight.clear();
  attachmentBlobs.clear();
}

/** Everyone seen in already-loaded lists and threads; feeds recipient suggestions without extra requests. */
export function knownParticipants(): MailboxParticipant[] {
  const seen = new Map<string, MailboxParticipant>();
  const add = (p?: MailboxParticipant) => {
    const address = p?.address?.trim().toLowerCase();
    if (!address) return;
    const existing = seen.get(address);
    if (!existing || (!existing.name && p?.name)) seen.set(address, { address, name: p?.name });
  };
  for (const page of lists.values()) for (const t of page.threads) t.participants?.forEach(add);
  for (const d of details.values()) {
    for (const m of d.messages) {
      add(m.from);
      m.to.forEach(add);
      m.cc?.forEach(add);
    }
  }
  return [...seen.values()];
}

const LISTS_KEY = 'cwy.mailbox.lists';

/** Survives reloads so the last-seen folder paints instantly while the network refresh runs. */
function readPersistedLists(): Record<string, ThreadListPage> {
  try {
    const raw = sessionStorage.getItem(LISTS_KEY);
    const parsed = raw ? (JSON.parse(raw) as { userId?: string; lists?: Record<string, ThreadListPage> }) : null;
    if (!parsed?.userId || parsed.userId !== currentUserId()) return {};
    return parsed.lists || {};
  } catch {
    return {};
  }
}

function persistLists() {
  try {
    const userId = currentUserId();
    if (!userId || lists.size === 0) {
      sessionStorage.removeItem(LISTS_KEY);
      return;
    }
    sessionStorage.setItem(LISTS_KEY, JSON.stringify({ userId, lists: Object.fromEntries(lists) }));
  } catch {
    /* storage full or unavailable */
  }
}

export function peekList(mailboxId: string, view: MailboxView): ThreadListPage | undefined {
  const key = listKey(mailboxId, view);
  const hit = lists.get(key);
  if (hit) return hit;
  const persisted = readPersistedLists()[key];
  if (persisted) lists.set(key, persisted);
  return persisted;
}

export function storeList(mailboxId: string, view: MailboxView, page: ThreadListPage) {
  lists.set(listKey(mailboxId, view), page);
  persistLists();
}

/** Drops cached folder lists; `keep` stays so the visible list can refresh without flashing a skeleton. */
export function invalidateLists(mailboxId: string, keep?: MailboxView) {
  for (const key of lists.keys()) {
    if (key.startsWith(`${mailboxId}:`) && key !== (keep ? listKey(mailboxId, keep) : '')) lists.delete(key);
  }
  persistLists();
}

export async function fetchList(mailboxId: string, view: ThreadFolder): Promise<ThreadListPage> {
  const page = await listThreads(mailboxId, { folder: view });
  storeList(mailboxId, view, page);
  return page;
}

export function peekDetail(mailboxId: string, threadId: string): ThreadDetail | undefined {
  return details.get(detailKey(mailboxId, threadId));
}

export function patchDetail(mailboxId: string, threadId: string, patch: (d: ThreadDetail) => ThreadDetail) {
  const key = detailKey(mailboxId, threadId);
  const current = details.get(key);
  if (current) details.set(key, patch(current));
}

export function isDetailFresh(mailboxId: string, threadId: string): boolean {
  const at = detailFetchedAt.get(detailKey(mailboxId, threadId));
  return !!at && Date.now() - at < DETAIL_FRESH_MS;
}

export function invalidateDetail(mailboxId: string, threadId: string) {
  const key = detailKey(mailboxId, threadId);
  details.delete(key);
  detailFetchedAt.delete(key);
}

/** Fetches a thread once; concurrent callers (hover prefetch + open) share the same request. */
export function fetchDetail(
  mailboxId: string,
  threadId: string,
  { force = false, load }: { force?: boolean; load?: (mailboxId: string, threadId: string) => Promise<ThreadDetail> } = {}
): Promise<ThreadDetail> {
  const key = detailKey(mailboxId, threadId);
  const cached = details.get(key);
  if (cached && !force) return Promise.resolve(cached);
  const pending = inflight.get(key);
  if (pending) return pending;
  const request = (load ?? getThread)(mailboxId, threadId)
    .then((data) => {
      details.set(key, data);
      detailFetchedAt.set(key, Date.now());
      return data;
    })
    .finally(() => inflight.delete(key));
  inflight.set(key, request);
  return request;
}

const MAX_CACHED_BLOBS = 30;
const attachmentBlobs = new Map<string, Promise<Blob>>();

/** Shared by thumbnails, preview and download so each attachment is fetched once per session. */
export function getAttachmentBlob(mailboxId: string, att: MailboxAttachment): Promise<Blob> {
  const key = `${mailboxId}:${att._id}`;
  const existing = attachmentBlobs.get(key);
  if (existing) return existing;
  const request = fetchAttachmentBlob(mailboxId, att).catch((err) => {
    attachmentBlobs.delete(key);
    throw err;
  });
  attachmentBlobs.set(key, request);
  if (attachmentBlobs.size > MAX_CACHED_BLOBS) {
    const oldest = attachmentBlobs.keys().next().value;
    if (oldest) attachmentBlobs.delete(oldest);
  }
  return request;
}

export function prefetchDetail(
  mailboxId: string,
  threadId: string,
  load?: (mailboxId: string, threadId: string) => Promise<ThreadDetail>
) {
  fetchDetail(mailboxId, threadId, { load }).catch(() => undefined);
}
